import type { MessageDescriptor } from 'react-intl';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { containsNetworkError } from '@confluence/network';

import { ObjectCommentsError } from './enum/ObjectCommentsError.ts';
import { i18n } from './objectCommentsi18n.ts';

type ParsedError = {
	message: string;
	statusCode?: number;
};

const parseError: (error: Error | ApolloErrorInterop) => ParsedError = (error) => {
	const { message } = error;
	let errorMessage: string;

	/* If this is a GraphQL error we need to extract the data out of it
	 * GraphQL errors that are returned from queries/mutations always have the same format:
	 * GraphQL error: <GUID>:[ <EXCEPTION>:] <REASON>
	 */
	if (message.indexOf('GraphQL error:') !== -1) {
		const reasonStartIdx = message.lastIndexOf(':') + 1;
		errorMessage = message.substr(reasonStartIdx, message.length - reasonStartIdx).trim();
	} else {
		errorMessage = message;
	}

	const parsedError: ParsedError = {
		message: errorMessage,
	};

	// If we have an ApolloErrorInterop, extract the status code and the authorization status
	const { graphQLErrors } = error as ApolloErrorInterop;
	if (graphQLErrors && graphQLErrors.length) {
		parsedError.statusCode = graphQLErrors?.[0]?.extensions?.statusCode;
	}

	return parsedError;
};

export const getTranslatedError: (
	error: Error | ApolloErrorInterop,
) => Required<MessageDescriptor> = (error) => {
	if (containsNetworkError(error)) {
		return i18n.networkError;
	}

	const parsedError = parseError(error);
	switch (parsedError.message) {
		case ObjectCommentsError.VIEW_NOT_PERMITTED:
			return i18n.viewNotPermitted;
		case ObjectCommentsError.NO_CREATE_PERMS:
			return i18n.createNotPermitted;
		default:
			return i18n.unexpectedError;
	}
};

export const isViewNotPermittedError = (translatedMessage: MessageDescriptor) =>
	translatedMessage.id === i18n.viewNotPermitted.id;

export const isNetworkError = (translatedMessage: MessageDescriptor) =>
	translatedMessage.id === i18n.networkError.id;

export const isCreateNotPermittedError = (translatedMessage: MessageDescriptor) =>
	translatedMessage.id === i18n.createNotPermitted.id;
